import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { SyntheticEvent } from "react";
import { isFulfilled } from "@reduxjs/toolkit";
import {
  Box,
  Button,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import { Clear as DeleteIcon } from "@mui/icons-material";

import { TalentCertificateDocument } from "../../../talents/talentAPI";
import { useAppDispatch } from "../../../../app/hooks";
import { getCertificateDocumentDownloadUrl } from "../../../talents/talentsSlice";
import { ReactComponent as DocumentIcon } from "../../../../common/icons/documentIcon.svg";
import { ReactComponent as EditIcon } from "../../../../common/icons/edit.svg";

type DocumentProps = {
  document: TalentCertificateDocument | File;
  index: number;
  removeDocument: (
    document: TalentCertificateDocument | File,
    index: number,
  ) => void;
  handleEditDocument?: (document: TalentCertificateDocument | File) => void;
};

const formatDocumentName = (document: TalentCertificateDocument) => {
  const firstUnderlineIndex = document.filename.indexOf("_");
  const name = document.filename.slice(firstUnderlineIndex + 1);
  return name;
};

export const WorkPermitDocumentComponent = ({
  document,
  index,
  removeDocument,
  handleEditDocument,
}: DocumentProps) => {
  const { t } = useTranslation("common");

  const dispatch = useAppDispatch();
  const { talentId } = useParams();

  const getDocumentDownloadURL = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (!(document instanceof File)) {
      dispatch(
        getCertificateDocumentDownloadUrl({
          pathspec: document.pathspec,
          talentId,
        }),
      ).then((action) => {
        if (isFulfilled(action)) {
          window.open(action.payload.url, "_blank", "noopener");
        }
      });
    }
  };

  const isNewDocument = document instanceof File;

  return (
    <Box display="flex" gap="10px" mb="0.5rem">
      <Box
        onClick={getDocumentDownloadURL}
        sx={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <DocumentIcon style={{ minWidth: "22px" }} />
        <Tooltip
          title={isNewDocument ? document.name : formatDocumentName(document)}
          enterDelay={500}
          enterNextDelay={500}
          leaveDelay={200}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "380px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              hyphens: "auto",
              wordBreak: "break-all",
            }}
          >
            <Typography
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {isNewDocument ? document.name : formatDocumentName(document)}
            </Typography>
            {!(document instanceof File) && document.status === "scanning" && (
              <CircularProgress
                size={14}
                sx={{ flexShrink: 0, marginLeft: 1 }}
              />
            )}
          </Box>
        </Tooltip>
      </Box>
      {handleEditDocument && (
        <Button
          sx={{
            textTransform: "none",
            padding: "4px",
          }}
          variant="text"
          startIcon={<EditIcon />}
          onClick={() => handleEditDocument(document)}
        >
          <Typography variant="secondary">{t("common:edit")}</Typography>
        </Button>
      )}
      {!(document instanceof File) && document.status !== "scanning" && (
        <Button
          sx={{
            textTransform: "none",
            padding: "4px",
          }}
          variant="text"
          startIcon={<DeleteIcon sx={{ color: "#00000085" }} />}
          onClick={() => removeDocument(document, index)}
        >
          <Typography variant="secondary">{t("common:delete")}</Typography>
        </Button>
      )}
    </Box>
  );
};
